import { Button, Form, Input, Skeleton, message } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import GroupBadge from "./GroupBadge";

const { TextArea } = Input;

/**
 * "About" tab — a free-text overview.
 *
 * On a branch board this shows two blocks when the school group has published
 * its own: the group's first, read-only, then the branch's. A group overview
 * never overwrites what a branch admin wrote, so both are kept.
 *
 * In `group` scope there is only one block — the group's own, editable.
 */
function BoardOverview({ api, board, isLoading, canEdit }) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);

  const isGroupScope = api.scope === "group";
  const ownOverview = board?.overview ?? "";
  const groupOverview = isGroupScope ? "" : (board?.group_overview ?? "");

  useEffect(() => {
    form.setFieldsValue({ overview: ownOverview });
  }, [ownOverview, form]);

  const mutation = useMutation((values) => api.saveOverview(values.overview), {
    onSuccess: () => {
      message.success("Overview saved");
      setEditing(false);
      queryClient.invalidateQueries(api.keys.board);
    },
    onError: (err) => {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Could not save overview",
      );
    },
  });

  if (isLoading) {
    return (
      <div className="pt-8">
        <div className="bg-white p-8 rounded-2xl">
          <Skeleton active />
        </div>
      </div>
    );
  }

  return (
    <div className="pt-8 space-y-6">
      {!!groupOverview && (
        <div className="bg-white w-full rounded-2xl p-6">
          <div className="flex items-center gap-3 pb-3">
            <div style={{ color: "#E46E31" }} className="text-xl font-semibold">
              From your School Group
            </div>
            <GroupBadge />
          </div>
          <div
            style={{ color: "#616161", whiteSpace: "pre-wrap" }}
            className="text-base"
          >
            {groupOverview}
          </div>
          {board?.group_overview_updated_at && (
            <div className="text-xs text-gray-400 pt-4">
              Last updated{" "}
              {moment(board.group_overview_updated_at).format("MMMM Do YYYY")}
            </div>
          )}
        </div>
      )}

      <div className="bg-white w-full rounded-2xl p-6">
        <div className="flex items-center justify-between pb-4">
          <div style={{ color: "#E46E31" }} className="text-2xl font-semibold">
            {isGroupScope ? "About this Group" : "About this Class"}
          </div>
          {canEdit && !editing && (
            <Button type="primary" size="large" onClick={() => setEditing(true)}>
              {ownOverview ? "Edit" : "Add Overview"}
            </Button>
          )}
        </div>

        {editing ? (
          <Form form={form} layout="vertical" onFinish={mutation.mutate}>
            <Form.Item
              name="overview"
              label="Overview"
              rules={[
                { max: 5000, message: "Overview must be under 5000 characters" },
              ]}
            >
              <TextArea
                rows={6}
                className="w-full resize-none"
                placeholder={
                  isGroupScope
                    ? "Describe the approach every branch in the group follows."
                    : "Describe the environment, the age group, and what the children work on."
                }
              />
            </Form.Item>
            <div className="flex justify-end items-center pt-2">
              <div className="inline-flex items-center space-x-2">
                <Button
                  size="large"
                  onClick={() => {
                    setEditing(false);
                    form.setFieldsValue({ overview: ownOverview });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={mutation.isLoading}
                >
                  Save
                </Button>
              </div>
            </div>
          </Form>
        ) : (
          <div>
            <div
              style={{ color: "#616161", whiteSpace: "pre-wrap" }}
              className="text-base"
            >
              {ownOverview ||
                (isGroupScope
                  ? "No group overview has been added yet."
                  : "No overview has been added for this class yet.")}
            </div>
            {board?.updatedAt && (
              <div className="text-xs text-gray-400 pt-4">
                Last updated {moment(board.updatedAt).format("MMMM Do YYYY")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BoardOverview;
